function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable react/no-multi-comp */
import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import isNil from "lodash.isnil";
import { transformDataAttrsDictToProps } from "scripts/data_utils/transformDataAttrsDictToProps";
import { themes, sizes } from "./themes";
import { Container, Wrapper, Title, Icon, SimpleButton } from "./styles";
const Button = styled( /*#__PURE__*/React.memo(props => {
  const {
    className,
    onClick,
    icon,
    iconName,
    iconRight,
    id,
    title,
    variation,
    appearance,
    disabled,
    loading,
    type,
    url,
    tabIndex,
    target,
    size,
    value,
    isEllipsis,
    dataAttrs,
    buttonType,
    ...otherProps
  } = props;
  const buttonRef = /*#__PURE__*/React.createRef();
  const hasIconSymbol = !isNil(iconName);
  const hasIconComponent = !!icon;
  const hasIcon = hasIconSymbol || hasIconComponent;
  const hasTitle = !isNil(title);
  const hasCombo = hasIcon && hasTitle;
  const justIcon = hasIcon && !hasTitle;
  const justTitle = !hasIcon && hasTitle;
  const theme = { ...themes[appearance][variation],
    size: sizes[size],
    isLoading: loading
  };
  const isDisabled = disabled || loading;
  const commonProps = {
    id,
    className,
    disabled: isDisabled,
    onClick,
    value,
    theme,
    hasCombo,
    justIcon,
    iconRight,
    ...otherProps
  }; // To fix an event in GTM on the parent when clicking on a nested element

  const onFakeClick = event => {
    event.preventDefault();
    event.stopPropagation();
    buttonRef.current.click();
  };

  if (type !== "button") {
    commonProps.as = type;
    commonProps["aria-disabled"] = isDisabled;
    commonProps.tabIndex = isDisabled ? -1 : tabIndex;
    commonProps.role = "button";
  } else {
    commonProps.type = buttonType;
  }

  const dataAttrsProps = transformDataAttrsDictToProps(dataAttrs, props);

  const ButtonBase = ({
    children,
    innerRef
  }) => {
    if (type === "link") {
      if (target === "_blank") {
        commonProps.rel = "noopener noreferrer";
      }

      return /*#__PURE__*/React.createElement(Container, _extends({}, commonProps, dataAttrsProps, {
        as: "a",
        href: isDisabled ? undefined : url,
        target: target,
        ref: innerRef
      }), children);
    }

    return /*#__PURE__*/React.createElement(Container, _extends({}, commonProps, dataAttrsProps, {
      ref: innerRef
    }), children);
  };

  const IconComponent = hasIconComponent ? icon : () => /*#__PURE__*/React.createElement(Icon, {
    name: iconName,
    theme: theme,
    size: 36,
    onClick: onFakeClick
  });

  const renderIcon = () => {
    return hasIcon && /*#__PURE__*/React.createElement(IconComponent, null);
  };

  const [_sizeIcon, _sizeJustIcon] = theme.size.iconSizes;
  let sizeIcon = _sizeIcon;

  if (justIcon) {
    sizeIcon = _sizeJustIcon;
  } // If the button does not have an icon, then we use a simple html without nesting


  if (justTitle) {
    if (type === "link") {
      if (target === "_blank") {
        commonProps.rel = "noopener noreferrer";
      }

      return /*#__PURE__*/React.createElement(SimpleButton, _extends({}, dataAttrsProps, commonProps, {
        isEllipsis: isEllipsis,
        as: "a",
        href: isDisabled ? undefined : url,
        target: target
      }), title);
    }

    return /*#__PURE__*/React.createElement(SimpleButton, _extends({}, dataAttrsProps, commonProps, {
      isEllipsis: isEllipsis
    }), title);
  }

  return /*#__PURE__*/React.createElement(ButtonBase, {
    innerRef: buttonRef
  }, /*#__PURE__*/React.createElement(Wrapper, {
    reverse: iconRight || loading,
    justIcon: hasIcon && !hasTitle,
    justTitle: justIcon,
    sizeIcon: sizeIcon,
    onClick: onFakeClick
  }, renderIcon(), hasTitle && /*#__PURE__*/React.createElement(Title, {
    theme: theme,
    isEllipsis: isEllipsis,
    onClick: onFakeClick
  }, title)));
}))``;

function requiredPropsCheck(props, propName, componentName) {
  if (isNil(props.iconName) && isNil(props.title)) {
    return new Error(`One of props 'iconName' or 'title' was not specified in '${componentName}'.`);
  }

  if (!isNil(props[propName]) && typeof props[propName] !== "string") {
    return new Error(`Invalid prop '${propName}' supplied to '${componentName}', expected 'string'.`);
  }

  if (!isNil(props[propName]) && props[propName].length === 0) {
    return new Error(`Invalid prop '${propName}' supplied to '${componentName}', expected not empty string.`);
  }
}

if (process.env.NODE_ENV !== "production") {
  Button.displayName = "Button";
}

Button.propTypes = {
  appearance: PropTypes.oneOf(Object.keys(themes)),
  className: PropTypes.string,
  dataAttrs: PropTypes.object,
  disabled: PropTypes.bool,
  iconName: requiredPropsCheck,
  iconRight: PropTypes.bool,
  id: PropTypes.string,
  isEllipsis: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseMove: PropTypes.func,
  onMouseOut: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseUp: PropTypes.func,
  size: PropTypes.string,
  tabIndex: PropTypes.number,
  target: PropTypes.string,
  title: requiredPropsCheck,
  type: PropTypes.string,
  variation: PropTypes.oneOf(Object.keys(themes.flat))
};
Button.defaultProps = {
  appearance: "flat",
  disabled: false,
  iconRight: false,
  isEllipsis: true,
  loading: false,
  size: "medium",
  tabIndex: 0,
  type: "button",
  variation: "default",
  buttonType: "submit",
  dataAttrs: {}
};
export default Button;